/* Verify current Downpay terms before theme-owned cart checkout.
   Never invent a deposit, clear the cart, or replace an existing purchase option. */
(() => {
  'use strict';
  if (window.bwDepositCheckout) return;
  const section = 'bw-cart-deposit-state';
  const displayKey = 'Deposit', legacyDisplayKey = 'Plan deposit / item', displayOwner = '_bw_deposit_display';
  const money = n => Number.isSafeInteger(n) && n >= 0;
  const validId = id => typeof id === 'string' && /^[1-9]\d*$/.test(id);
  const partial = plan => plan && money(plan.price) && money(plan.checkoutCharge) &&
    money(plan.remainingBalance) && plan.remainingBalance > 0 && plan.checkoutCharge < plan.price;
  const message = 'We could not verify your deposit. Your cart has been kept. Please try again before checking out.';
  const fail = text => { throw new Error(text || message); };
  const properties = value => {
    if (value == null) return [];
    const entries = Array.isArray(value) ? value : Object.entries(value);
    if (!entries.every(pair => Array.isArray(pair) && pair.length === 2 &&
      typeof pair[0] === 'string' && (pair[1] === null || ['string', 'boolean'].includes(typeof pair[1]) ||
        (typeof pair[1] === 'number' && Number.isFinite(pair[1]))))) fail();
    return entries.filter(([key, value]) =>
      !([displayKey, legacyDisplayKey, displayOwner].includes(key) && (value === '' || value === null)))
      .sort(([a], [b]) => a.localeCompare(b));
  };
  function validate(cart) {
    if (!cart || cart.version !== 1 || !Array.isArray(cart.items) ||
      !money(cart.itemCount) || !money(cart.total) || !money(cart.checkoutCharge) ||
      cart.checkoutCharge > cart.total) fail();
    const keys = new Set();
    let count = 0;
    for (const item of cart.items) {
      if (!item || typeof item.key !== 'string' || !item.key || keys.has(item.key) ||
        !validId(item.variantId) || !validId(item.productId) ||
        !Number.isSafeInteger(item.quantity) || item.quantity < 1 ||
        typeof item.requiresDeposit !== 'boolean' || typeof item.title !== 'string' ||
        !Array.isArray(item.downpayPlans) ||
        (item.selectedPlanId !== null && !validId(item.selectedPlanId))) fail();
      keys.add(item.key);
      properties(item.properties);
      count += item.quantity;
      const plans = new Set();
      for (const plan of item.downpayPlans) {
        if (!plan || !validId(plan.id) || plans.has(plan.id)) fail();
        plans.add(plan.id);
      }
    }
    if (count !== cart.itemCount || !Number.isSafeInteger(count)) fail();
    if (!count) fail('Your cart is empty. Please add a product before checking out.');
    return cart;
  }
  function requiredChanges(cart) {
    return cart.items.flatMap(item => {
      const selectedDownpay = item.downpayPlans.find(plan => plan.id === item.selectedPlanId);
      if (!item.requiresDeposit && !selectedDownpay) return [];
      if (selectedDownpay && partial(selectedDownpay) && partial(item.selectedAllocation)) return [];
      if (item.selectedPlanId !== null) {
        fail('Please review the purchase option for ' + item.title + ' on its product page. Checkout has been stopped to avoid charging the full price.');
      }
      const eligible = item.downpayPlans.filter(partial);
      if (eligible.length !== 1) {
        fail('Please select a valid deposit option for ' + item.title + ' on its product page before checking out. Your cart has been kept.');
      }
      return [{ key: item.key, quantity: item.quantity, planId: eligible[0].id }];
    });
  }
  // A plan change may merge matching lines and change keys. Compare quantities
  // by product, variant, properties and plan, rather than by position or key.
  function signature(cart, change) {
    const rows = new Map();
    for (const item of cart.items) {
      const plan = change?.key === item.key && change.planId !== undefined ? change.planId : item.selectedPlanId;
      const saved = change?.key === item.key && change.properties ? change.properties : item.properties;
      const key = JSON.stringify([item.productId, item.variantId, properties(saved), plan]);
      rows.set(key, (rows.get(key) || 0) + item.quantity);
    }
    return JSON.stringify([...rows].sort(([a], [b]) => a.localeCompare(b)));
  }
  function route(suffix) {
    const root = window.Shopify?.routes?.root;
    if (typeof root !== 'string' || !root.endsWith('/')) fail();
    const url = new URL(root + suffix, window.location.origin);
    if (url.origin !== window.location.origin) fail();
    return url;
  }
  async function request(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, { ...options, credentials: 'same-origin',
        cache: 'no-store', signal: controller.signal });
      if (!response.ok) fail();
      return await response.json();
    } finally { clearTimeout(timer); }
  }
  async function readCart() {
    const url = route('cart');
    url.searchParams.set('sections', section);
    const response = await request(url.href);
    if (typeof response?.[section] !== 'string') fail();
    const doc = new DOMParser().parseFromString(response[section], 'text/html');
    const nodes = doc.querySelectorAll('[data-bw-cart-deposit-state]');
    if (nodes.length !== 1) fail();
    return validate(JSON.parse(nodes[0].textContent));
  }
  async function prepare() {
    let cart = await readCart();
    let pending = requiredChanges(cart);
    const limit = cart.items.length;
    for (let attempt = 0; pending.length && attempt < limit; attempt += 1) {
      // Selling-plan changes require a 1-based line index. Resolve it from a
      // fresh cart under the shared mutation lock, never from stale page markup.
      const fresh = await readCart();
      if (signature(fresh) !== signature(cart)) fail('Your cart changed. Please review it and try checkout again.');
      cart = fresh;
      pending = requiredChanges(cart);
      if (!pending.length) break;
      const change = pending[0];
      const line = cart.items.findIndex(item => item.key === change.key) + 1;
      if (line < 1) fail();
      const expected = signature(cart, change);
      const response = await request(route('cart/change.js').href, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity: change.quantity, selling_plan: change.planId }),
      });
      if (response?.errors || response?.status || !Array.isArray(response?.items)) fail();
      const next = await readCart();
      if (signature(next) !== expected) fail('Your cart changed while we verified the deposit. Please review it and try checkout again.');
      const remaining = requiredChanges(next);
      if (remaining.length >= pending.length) fail();
      cart = next;
      pending = remaining;
    }
    if (pending.length) fail();
    // Public line properties carry the plan amount into Shopify's checkout UI.
    // Preserve every unrelated property; amounts never determine the payment.
    const displayChanges = current => current.items.flatMap(item => {
      const saved = Object.fromEntries(properties(item.properties));
      const selected = item.downpayPlans.find(plan => plan.id === item.selectedPlanId);
      const value = selected && partial(item.selectedAllocation) ? item.depositDisplay : '';
      if (value && (typeof value !== 'string' || value.length > 120)) fail();
      if (selected && !value) fail('Please reload your cart so we can verify the deposit display.');
      const owned = saved[displayOwner] === 'v1' || saved[displayOwner] === 'v2';
      if (!value && !owned) return [];
      if (saved[displayOwner] && !owned) fail();
      if (saved[displayKey] && saved[displayOwner] !== 'v2') fail('Please review your cart details before checkout.');
      if (value && saved[displayOwner] === 'v2' && saved[displayKey] === value) return [];
      const updated = { ...saved, [displayKey]: value || '', [displayOwner]: value ? 'v2' : '' };
      if (saved[displayOwner] === 'v1') updated[legacyDisplayKey] = '';
      return [{ key: item.key, quantity: item.quantity, properties: updated }];
    });
    const displayLimit = cart.items.length * 2;
    for (let attempt = 0; attempt < displayLimit; attempt += 1) {
      const fresh = await readCart();
      if (signature(fresh) !== signature(cart) || requiredChanges(fresh).length) fail();
      cart = fresh;
      const changes = displayChanges(cart);
      if (!changes.length) break;
      const change = changes[0];
      const expected = signature(cart, change);
      const response = await request(route('cart/change.js').href, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: change.key, quantity: change.quantity, properties: change.properties }),
      });
      if (response?.errors || response?.status || !Array.isArray(response?.items)) fail();
      const next = await readCart();
      if (signature(next) !== expected || requiredChanges(next).length || (displayChanges(next).length >= changes.length && next.items.length >= cart.items.length)) fail();
      cart = next;
    }
    if (displayChanges(cart).length) fail();
    // Read back even when no repair was needed; never rely on cached page markup.
    const finalCart = await readCart();
    if (signature(finalCart) !== signature(cart) || requiredChanges(finalCart).length || displayChanges(finalCart).length) {
      fail('Your cart changed while we verified the deposit. Please review it and try checkout again.');
    }
    return finalCart;
  }
  function showError(trigger, text) {
    const host = trigger?.closest('.bw-checkout-card, .cart__ctas') ||
      document.getElementById('cart-errors') || document.body;
    let error = host.querySelector('[data-bw-deposit-error]');
    if (!error) {
      error = document.createElement('p');
      error.dataset.bwDepositError = '';
      error.setAttribute('role', 'alert');
      error.setAttribute('tabindex', '-1');
      host.appendChild(error);
    }
    error.textContent = text;
    error.hidden = false;
    error.focus();
  }
  let inFlight = null;
  function checkout(trigger) {
    if (inFlight) return inFlight;
    const controls = [...document.querySelectorAll('[data-bw-cart-checkout], .contentHolder--checkout')];
    const prior = controls.map(control => [control, control.disabled, control.getAttribute('aria-busy')]);
    controls.forEach(control => {
      if ('disabled' in control) control.disabled = true;
      control.setAttribute('aria-busy', 'true');
    });
    document.querySelectorAll('[data-bw-deposit-error]').forEach(error => { error.hidden = true; });
    inFlight = (async () => {
      await Promise.resolve();
      try {
        if (typeof window.bwCartGuard?.run !== 'function') fail();
        await window.bwCartGuard.run(async () => {
          await prepare();
          window.location.assign(route('checkout').href);
        });
        return true;
      } catch (error) {
        showError(trigger, error instanceof Error ? error.message : message);
        return false;
      } finally {
        prior.forEach(([control, disabled, busy]) => {
          if ('disabled' in control) control.disabled = disabled;
          if (busy === null) control.removeAttribute('aria-busy');
          else control.setAttribute('aria-busy', busy);
        });
        inFlight = null;
      }
    })();
    return inFlight;
  }
  window.bwDepositCheckout = { checkout };
  document.addEventListener('click', event => {
    const trigger = event.target.closest?.('[data-bw-cart-checkout]');
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!trigger.disabled) checkout(trigger);
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!['cart', 'CartDrawer-Form', 'cart-notification-form'].includes(form.id)) return;
    if (event.submitter?.name !== 'checkout' && !form.querySelector('input[name="checkout"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(event.submitter || form);
  }, true);
  // Login returns to the cart, where deposit verification runs again.
  if (document.body.classList.contains('page-cart') &&
      new URLSearchParams(window.location.search).get('bw_checkout') === '1') {
    checkout(document.querySelector('[data-bw-cart-checkout]'));
  }
})();
